// ===========================================================================
// Live headcounts for the rooms the site keeps open, on the landing page.
//
// "14 people in the quiet room right now" is the strongest front-door line in
// the research (Study Together's "40,504 online"), so it is fetched from
// GET /api/rooms/standing -- adult band only; the server never publishes teen
// counts -- and refreshed while the landing is on screen. When the fetch
// fails, or the feature is off, the block simply is not there: a line that
// says "0 people" or "could not load" is worse than no line. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { el } from '../dom.js';

// No import of config.js here: it reads import.meta.env at load time, which
// only Vite provides, and the pure helpers below are unit-tested under plain
// Node. The caller (landing.js) passes the server origin in.

/** How often the landing asks again while it is showing. */
export const ROOM_COUNTS_REFRESH_MS = 60_000;

const MODE_LABEL = { video: 'Video', audio: 'Audio', text: t('Text') };

/** "14 people in the quiet room right now", or null when there is nothing
 *  honest to say. The quiet room is found by its flag, not its name. */
export function quietRoomLine(rooms) {
  const quiet = (rooms || []).find((r) => r && r.quiet);
  if (!quiet) return null;
  const n = Number(quiet.members) || 0;
  if (n <= 0) return null; // "0 people" reads as broken, not as an invitation
  return `${n} ${n === 1 ? 'person' : 'people'} in ${quiet.name || 'the quiet room'} right now`;
}

/** The short count for one room row. Never "0 people". */
export function describeRoom(r) {
  const here = Number(r?.members) || 0;
  const watching = Number(r?.watching) || 0;
  if (here <= 0) return 'Quiet right now';
  const parts = [`${here} here`];
  if (watching > 0) parts.push(`${watching} watching`);
  return parts.join(', ');
}

/** Where a row goes when it is tapped, or null for a row that is only a
 *  count. Only a promoted room has somewhere to go: it is somebody's room
 *  rather than one of the site's, so its code is the whole point of listing
 *  it. A code that does not look like a code gets no link at all. */
export function roomHref(r) {
  const code = typeof r?.code === 'string' ? r.code.toUpperCase() : '';
  if (!r?.promoted || !/^[A-Z0-9]{3,12}$/.test(code)) return null;
  return `/r/${code}`;
}

/** A language tag for the row, or null. Short and honest: the tag, upper case. */
export function langTag(lang) {
  return typeof lang === 'string' && lang ? lang.toUpperCase().slice(0, 8) : null;
}

/** One fetch. Resolves to the rooms array, or null on any failure.
 *  `base` is the server origin ('' means the page's own). */
export async function fetchStandingCounts(fetchImpl = globalThis.fetch, base = '') {
  try {
    const res = await fetchImpl(`${base}/api/rooms/standing`);
    if (!res.ok) return null;
    const body = await res.json();
    return Array.isArray(body?.rooms) ? body.rooms : null;
  } catch {
    return null;
  }
}

/**
 * Fill `host` with the quiet-room line and the compact list, and keep it
 * fresh while `host` is in the document. Stops on its own once the landing is
 * gone, so leaving for a chat does not leave a timer polling behind it.
 * @returns {{ stop: () => void }}
 */
export function mountRoomCounts(host, opts = {}) {
  const refreshMs = opts.refreshMs || ROOM_COUNTS_REFRESH_MS;
  const fetchImpl = opts.fetch || globalThis.fetch;
  const base = opts.serverHttp || '';
  let timer = null;

  function draw(rooms) {
    if (!rooms || !rooms.length) {
      host.hidden = true;
      host.replaceChildren();
      return;
    }
    const line = quietRoomLine(rooms);
    // filter(Boolean), because replaceChildren does NOT skip a null the way
    // el()'s children do: it stringifies it. With no quiet-room line -- which
    // is most of the time, since an empty quiet room says nothing -- the word
    // "null" was printed above the heading.
    const children = [
      line ? el('p', { class: 'open-rooms-line', text: line }) : null,
      el('p', { class: 'open-rooms-title', text: t('Rooms that are always open') }),
      el('ul', { class: 'open-rooms-list' }, rooms.map((r) => {
        const href = roomHref(r);
        const inner = [
          el('span', { class: 'open-rooms-name', text: r.name || r.slug }),
          // Said out loud, on every promoted row. A paid placement that looks
          // like the site's own recommendation is the one thing this feature
          // must not become, and the word costs a row nothing.
          r.promoted ? el('span', { class: 'room-promoted', text: t('Promoted') }) : null,
          el('span', { class: `room-mode mode-${r.mode || 'video'}`, text: MODE_LABEL[r.mode] || 'Video' }),
          langTag(r.lang) ? el('span', { class: 'room-lang', text: langTag(r.lang) }) : null,
          el('span', { class: 'open-rooms-count', text: describeRoom(r) }),
        ].filter(Boolean);
        // A standing room is a count and nothing more: it has its own card in
        // the hub, and a second way in from here would be a second thing to
        // keep right. A promoted room has no card anywhere, so the row IS the
        // way in.
        return el('li', { class: 'open-rooms-row' }, [
          href ? el('a', { class: 'open-rooms-link', href }, inner) : el('span', { class: 'open-rooms-inner' }, inner),
        ]);
      })),
    ];
    host.replaceChildren(...children.filter(Boolean));
    host.hidden = false;
    // This block refreshes itself every minute, so it says so. It used to be
    // covered by an aria-live on the whole application, which announced
    // every screen change on the site as an update and so announced nothing
    // usefully.
    // Optional, because the drawing is unit-tested against a plain object
    // standing in for the element, and an announcement is not what those
    // tests are about.
    host.setAttribute?.('role', 'status');
    host.setAttribute?.('aria-live', 'polite');
  }

  async function tick() {
    if (!host.isConnected) {
      stop();
      return;
    }
    draw(await fetchStandingCounts(fetchImpl, base));
  }

  function stop() {
    if (timer) clearInterval(timer);
    timer = null;
  }

  host.hidden = true; // nothing until the first answer arrives
  tick();
  timer = setInterval(tick, refreshMs);
  return { stop };
}

export default { mountRoomCounts, fetchStandingCounts, quietRoomLine, describeRoom, langTag, roomHref };
